/**
 * Query hook for checking promotion date conflicts.
 *
 * Fires on demand (on end date blur), not automatically.
 * Returns overlapping active Pricing Rules for the proposed date range.
 */

import { useQuery } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// ── Types ────────────────────────────────────────────────────

export interface ConflictingRule {
  name: string;
  title: string;
  valid_from: string | null;
  valid_upto: string | null;
  rate_or_discount: string;
  discount_percentage: number | null;
  rate: number | null;
}

// ── Hook ─────────────────────────────────────────────────────

export function useCheckConflicts(
  itemCode: string,
  validFrom: string,
  validUpto: string | null,
  enabled: boolean
) {
  return useQuery<ConflictingRule[]>({
    queryKey: ["promotion-conflicts", itemCode, validFrom, validUpto ?? ""],
    enabled: enabled && !!itemCode && !!validFrom,
    queryFn: () =>
      frappeMethod<ConflictingRule[]>(
        "zaviago_backend.api.pricing_promotion_actions.check_promotion_conflicts",
        {
          item_code: itemCode,
          valid_from: validFrom,
          ...(validUpto ? { valid_upto: validUpto } : {}),
        }
      ),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
}
